import { useState, useEffect } from "react";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

interface CountdownResult {
  timeRemaining: string;
  isExpired: boolean;
  showTimer: boolean;
  minutes: number;
  seconds: number;
}

/** Only re-renders when isExpired/showTimer actually change — not every second. */
export function useExpirationStatus(expiresAt: string) {
  const calc = () => {
    if (!expiresAt) return { isExpired: false, showTimer: false };
    const diff = new Date(expiresAt).getTime() - Date.now();
    return { isExpired: diff <= 0, showTimer: diff > 0 && diff <= TWENTY_FOUR_HOURS_MS };
  };

  const [status, setStatus] = useState(calc);

  useEffect(() => {
    const current = calc();
    setStatus(current);
    if (!expiresAt || current.isExpired || current.showTimer === false) return;
    const msLeft = new Date(expiresAt).getTime() - Date.now();
    const t = setTimeout(() => setStatus({ isExpired: true, showTimer: false }), msLeft);
    return () => clearTimeout(t);
  }, [expiresAt]);

  return status;
}

export function useCountdown(expiresAt: string): CountdownResult {
  const [timeRemaining, setTimeRemaining] = useState<CountdownResult>({
    timeRemaining: "00:00",
    isExpired: false,
    showTimer: false,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeRemaining = (): CountdownResult => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        return {
          timeRemaining: "00:00",
          isExpired: true,
          showTimer: false,
          minutes: 0,
          seconds: 0,
        };
      }

      if (difference > TWENTY_FOUR_HOURS_MS) {
        return {
          timeRemaining: "00:00",
          isExpired: false,
          showTimer: false,
          minutes: 0,
          seconds: 0,
        };
      }

      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      const formatted = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

      return {
        timeRemaining: formatted,
        isExpired: false,
        showTimer: true,
        minutes,
        seconds,
      };
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      const result = calculateTimeRemaining();
      setTimeRemaining(result);

      if (result.isExpired) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeRemaining;
}
